import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { 
  LayoutDashboard, Users, Calendar, Package, FileText, Menu, Building2,
  MessageSquare, Settings as SettingsIcon, Database, LogOut, UserCheck, User, MessageCircle, UsersRound, Lightbulb
} from 'lucide-react';
import './App.css';

// Pages
import Dashboard from './pages/Dashboard';
import Employees from './pages/Employees';
import EmployeeDetail from './pages/EmployeeDetail';
import Scadenze from './pages/Scadenze';
import Assets from './pages/Assets';
import AssetDetail from './pages/AssetDetail';
import Documents from './pages/Documents';
import HSEAziendale from './pages/HSEAziendale';
import Login from './pages/Login';
import UsersManagement from './pages/UsersManagement';
import Settings from './pages/Settings';
import BackupManagement from './pages/BackupManagement';
import Board from './pages/Board';
import MyProfile from './pages/MyProfile';
import Chat from './pages/Chat';
import Community from './pages/Community';
import Suggestions from './pages/Suggestions';

function Sidebar({ isOpen, onClose, currentUser }) {
  const location = useLocation();
  
  const navItems = [
    { path: '/', icon: MessageSquare, label: 'Bacheca', testId: 'nav-board', roles: ['all'] },
    { path: '/community', icon: UsersRound, label: 'Community', testId: 'nav-community', roles: ['all'] },
    { path: '/suggestions', icon: Lightbulb, label: 'Suggerimenti', testId: 'nav-suggestions', roles: ['all'] },
    { path: '/chat', icon: MessageCircle, label: 'Chat', testId: 'nav-chat', roles: ['all'] },
    { path: '/my-profile', icon: User, label: 'Il Mio Profilo', testId: 'nav-my-profile', roles: ['all'] },
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', testId: 'nav-dashboard', roles: ['all'] },
    { path: '/employees', icon: Users, label: 'Dipendenti', testId: 'nav-employees', roles: ['admin', 'hse_manager'] },
    { path: '/scadenze', icon: Calendar, label: 'Scadenze', testId: 'nav-scadenze', roles: ['admin', 'hse_manager'] },
    { path: '/assets', icon: Package, label: 'Asset', testId: 'nav-assets', roles: ['admin', 'hse_manager'] },
    { path: '/hse-aziendale', icon: Building2, label: 'HSE Aziendale', testId: 'nav-hse-aziendale', roles: ['admin', 'hse_manager'] },
    { path: '/documents', icon: FileText, label: 'Documenti', testId: 'nav-documents', roles: ['admin', 'hse_manager'] },
  ];
  
  const adminNavItems = [
    { path: '/users', icon: UserCheck, label: 'Gestione Utenti', testId: 'nav-users', roles: ['admin'] },
    { path: '/backup', icon: Database, label: 'Backup', testId: 'nav-backup', roles: ['admin'] },
    { path: '/settings', icon: SettingsIcon, label: 'Impostazioni', testId: 'nav-settings', roles: ['admin'] },
  ];
  
  const canAccessRoute = (item) => {
    if (!currentUser) return false;
    if (item.roles.includes('all')) return true;
    return item.roles.includes(currentUser.role);
  };
  
  return (
    <div className={`sidebar ${isOpen ? 'open' : ''}`}>
      <div className="sidebar-logo" data-testid="logo">
        <div className="flex items-center gap-3">
          <img 
            src="/main-solution-logo.png" 
            alt="Main Solution" 
            className="h-10 w-auto object-contain"
            style={{ filter: 'brightness(1.1)' }}
          />
          <div className="flex flex-col">
            <span className="text-2xl font-bold" style={{ 
              background: 'linear-gradient(135deg, #00A3E0 0%, #0077BE 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              💙 Life
            </span>
            <span className="text-xs text-muted-foreground font-normal">HR & HSE Portal</span>
          </div>
        </div>
      </div>
      <nav className="sidebar-nav">
        {navItems.filter(canAccessRoute).map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
              onClick={onClose}
              data-testid={item.testId}
            >
              <Icon size={20} />
              <span>{item.label}</span>
            </Link>
          );
        })}
        
        {/* Admin Section */}
        {currentUser?.role === 'admin' && (
          <>
            <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase">
              Amministrazione
            </div>
            {adminNavItems.filter(canAccessRoute).map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
                  onClick={onClose}
                  data-testid={item.testId}
                >
                  <Icon size={20} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </>
        )}
      </nav>
      
      <div className="p-4 border-t border-border mt-auto">
        <div className="text-xs text-muted-foreground text-center">
          <p className="font-semibold mb-1">Main Solution S.r.l.</p>
          <p>Energie Rinnovabili</p>
          <p className="mt-2">© 2015-2025</p>
        </div>
      </div>
    </div>
  );
}

function Header({ onMenuClick, currentUser, onLogout }) {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [imageLoaded, setImageLoaded] = React.useState(false);
  const [imageError, setImageError] = React.useState(false);
  const imgRef = React.useRef(null);
  
  const getRoleLabel = (role) => {
    if (role === 'admin') return 'Amministratore';
    if (role === 'hse_manager') return 'HSE Manager';
    return 'Dipendente';
  };
  
  const fotoUrl = currentUser?.employee?.foto_url;

  // Reset image state when user changes
  React.useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [fotoUrl]);
  
  // Check if image is already cached/loaded
  React.useEffect(() => {
    if (imgRef.current && imgRef.current.complete && imgRef.current.naturalWidth > 0) {
      setImageLoaded(true);
    }
  }, [fotoUrl]);

  return (
    <header className="header">
      <div className="flex items-center gap-4">
        <button
          className="md:hidden btn-secondary"
          onClick={onMenuClick}
          data-testid="mobile-menu-button"
        >
          <Menu size={20} />
        </button>
        <div>
          <h1 className="text-lg font-semibold">Portale 💙 Life</h1>
          <p className="text-sm text-muted-foreground">Sistema di gestione HR e HSE</p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        {currentUser && (
          <>
            <div className="text-right">
              <p className="text-sm font-medium">{currentUser.nome} {currentUser.cognome}</p>
              <p className="text-xs text-muted-foreground">{getRoleLabel(currentUser.role)}</p>
            </div>
            <div className="relative w-10 h-10">
              {fotoUrl && !imageError && (
                <img
                  ref={imgRef}
                  src={`${API_URL}${fotoUrl}`}
                  alt={`${currentUser.nome} ${currentUser.cognome}`}
                  className={`w-10 h-10 rounded-full object-cover border-2 border-white shadow absolute inset-0 z-10 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                  style={{ transition: 'opacity 0.3s ease-in-out' }}
                  data-testid="user-avatar-img"
                  onLoad={(e) => {
                    if (e.target.naturalWidth > 0) {
                      setImageLoaded(true);
                    }
                  }}
                  onError={() => setImageError(true)}
                />
              )}
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-white absolute inset-0 ${imageLoaded ? 'opacity-0' : 'opacity-100'}`}
                style={{ 
                  background: 'linear-gradient(135deg, #00A3E0 0%, #0077BE 100%)',
                  transition: 'opacity 0.3s ease-in-out'
                }}
                data-testid="user-avatar"
              >
                {currentUser.nome?.charAt(0)}{currentUser.cognome?.charAt(0)}
              </div>
            </div>
            <button
              className="btn btn-secondary"
              onClick={onLogout}
              data-testid="logout-button"
            >
              <LogOut size={18} />
            </button>
          </>
        )}
      </div>
    </header>
  );
}

// Protected Route Component
function ProtectedRoute({ children }) {
  const token = localStorage.getItem('token');
  const user = localStorage.getItem('user');
  
  if (!token || !user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  useEffect(() => {
    // Check if user is logged in and fetch full user data
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    
    if (token && user) {
      // Fetch full user data with employee info
      fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(res => res.ok ? res.json() : null)
        .then(userData => {
          if (userData) {
            setCurrentUser(userData);
            // Update localStorage with employee data
            localStorage.setItem('user', JSON.stringify(userData));
          } else {
            setCurrentUser(JSON.parse(user));
          }
        })
        .catch(() => {
          setCurrentUser(JSON.parse(user));
        });
    }
  }, [API_URL]);
  
  const handleLogin = (user) => {
    // Fetch full user data including employee
    const token = localStorage.getItem('token');
    if (token) {
      fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(res => res.ok ? res.json() : null)
        .then(userData => {
          if (userData) {
            setCurrentUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));
          } else {
            setCurrentUser(user);
          }
        })
        .catch(() => {
          setCurrentUser(user);
        });
    } else {
      setCurrentUser(user);
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setCurrentUser(null);
    window.location.href = '/login';
  };
  
  return (
    <Router>
      <Routes>
        {/* Public Route */}
        <Route path="/login" element={<Login onLogin={handleLogin} />} />
        
        {/* Protected Routes */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <div className="app-container">
                <Sidebar 
                  isOpen={sidebarOpen} 
                  onClose={() => setSidebarOpen(false)}
                  currentUser={currentUser}
                />
                <div className="main-content">
                  <Header 
                    key={currentUser?.employee?.foto_url || currentUser?._id || 'header'}
                    onMenuClick={() => setSidebarOpen(!sidebarOpen)}
                    currentUser={currentUser}
                    onLogout={handleLogout}
                  />
                  <main className="page-content">
                    <Routes>
                      <Route path="/" element={<Board />} />
                      <Route path="/community" element={<Community />} />
                      <Route path="/suggestions" element={<Suggestions />} />
                      <Route path="/chat" element={<Chat />} />
                      <Route path="/my-profile" element={<MyProfile />} />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/employees" element={<Employees />} />
                      <Route path="/employees/:id" element={<EmployeeDetail />} />
                      <Route path="/scadenze" element={<Scadenze />} />
                      <Route path="/assets" element={<Assets />} />
                      <Route path="/assets/:id" element={<AssetDetail />} />
                      <Route path="/hse-aziendale" element={<HSEAziendale />} />
                      <Route path="/documents" element={<Documents />} />
                      <Route path="/users" element={<UsersManagement />} />
                      <Route path="/settings" element={<Settings />} />
                      <Route path="/backup" element={<BackupManagement />} />
                    </Routes>
                  </main>
                </div>
              </div>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
